import type {
  GroupResponsibleState,
  Participant,
  Room,
  RoomFilterType,
  RoomParticipantFilterType,
} from '../../domain/types'
import { RoomParticipantFilterValues } from '../../domain/types'
import { sortParticipants } from './utils'

function sortRooms(rooms: Room[], participants: Participant[]): Room[] {
  return rooms.sort((a, b) => {
    const aHasAssigned = roomHasAssignedParticipants(a, participants)
    const bHasAssigned = roomHasAssignedParticipants(b, participants)

    if (aHasAssigned !== bHasAssigned) {
      return aHasAssigned ? -1 : 1
    }

    return a.name.localeCompare(b.name)
  })
}

function roomHasAssignedParticipants(room: Room, participants: Participant[]): boolean {
  return room.participantsInfo.some(info =>
    participants.some(participant => participant.id === info.id && participant.hasRoomAssigned),
  )
}

export function getRoomExtraInfo(state: GroupResponsibleState) {
  const activeRoom = getActiveRoom(state)

  return {
    name: activeRoom?.name || '',
    participants: activeRoom?.participantsInfo?.map(p => p.name) || [],
    type: activeRoom?.type || '-',
    accommodationName: activeRoom?.accommodationName || '-',
    maxOccupancy: activeRoom?.occupancy.max || 0,
    needsAction: activeRoom?.needsAction || false,
    pinCode: activeRoom?.pinCode,
  }
}

export function getRoomsCounts(state: GroupResponsibleState): {
  all: number
  notOccupied: number
  occupied: number
} {
  const { rooms } = state
  if (!rooms)
    return { all: 0, notOccupied: 0, occupied: 0 }

  const all = Object.keys(rooms).length

  const notOccupied = Object.keys(rooms).filter(
    room => rooms[room].occupancy.occupied !== rooms[room].occupancy.max,
  ).length

  const occupied = all - notOccupied

  return { all, notOccupied, occupied }
}

export function getFilteredRooms(state: GroupResponsibleState, filter: RoomFilterType, searchTerm = '') {
  const rooms = Object.values(state.rooms)
  const participants = Object.values(state.participants)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  let filteredRooms = rooms

  if (filter !== 'all') {
    filteredRooms = rooms.filter((room) => {
      const { occupied, max } = room.occupancy
      return filter === 'occupied' ? occupied === max : occupied !== max
    })
  }

  if (trimmedSearchTerm) {
    filteredRooms = filteredRooms.filter((room) => {
      const nameMatches = room.name.toLowerCase().includes(trimmedSearchTerm)
      if (nameMatches)
        return true

      const roomParticipants = participants.filter((participant) => {
        return room.participantsInfo.some(info => info.id === participant.id)
      })

      return roomParticipants.some(participant => participant.name.toLowerCase().includes(trimmedSearchTerm))
    })
  }

  return sortRooms(filteredRooms, participants)
}

export function getFilteredRoomParticipants(
  state: GroupResponsibleState,
  filter: RoomParticipantFilterType,
  searchTerm = '',
) {
  const participantsArray = Object.values(state.participants)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  return participantsArray
    .filter((participant) => {
      const isNotCancelled = !participant.isCancelled
      const matchesSearch = participant.name.toLowerCase().includes(trimmedSearchTerm)
      const matchesFilter
        = filter === RoomParticipantFilterValues.ALL
          || (filter === RoomParticipantFilterValues.ASSIGNED && participant.hasRoomAssigned)
          || (filter === RoomParticipantFilterValues.UNASSIGNED && !participant.hasRoomAssigned)
      return isNotCancelled && matchesSearch && matchesFilter
    })
    .sort(sortParticipants)
}

export function getRoomParticipantCounts(state: GroupResponsibleState) {
  const participantsArray = Object.values(state.participants).filter(p => !p.isCancelled)
  return {
    all: participantsArray.length,
    unassigned: participantsArray.filter(p => !p.hasRoomAssigned).length,
    assigned: participantsArray.filter(p => p.hasRoomAssigned).length,
  }
}

export function getActiveRoom(state: GroupResponsibleState): Room | null {
  const activeRoom = state.activeExtraInfoId ? state.rooms[state.activeExtraInfoId] : null
  return activeRoom || null
}

export function getAssignedParticipantsForRoom(state: GroupResponsibleState): string[] {
  const activeRoom = getActiveRoom(state)
  if (!activeRoom)
    return []

  if (state.stagedRoomAssignments?.[activeRoom.id]) {
    return state.stagedRoomAssignments[activeRoom.id].map(String)
  }

  return activeRoom.participantsInfo.map(participant => String(participant.bookingCustomerId))
}

export function canSelectParticipant(state: GroupResponsibleState, participantId: string): boolean {
  const isCurrentlySelected = state.selectedRoomParticipantIds.has(participantId)
  if (isCurrentlySelected)
    return true

  const activeRoom = getActiveRoom(state)
  if (!activeRoom)
    return false

  const maxOccupancy = activeRoom.occupancy.max
  const currentSelectionCount = state.selectedRoomParticipantIds.size

  return currentSelectionCount < maxOccupancy
}

export function isMaxOccupancyReached(state: GroupResponsibleState): boolean {
  const activeRoom = getActiveRoom(state)
  if (!activeRoom)
    return false

  return state.selectedRoomParticipantIds.size === activeRoom.occupancy.max
}

export function isOverCapacity(state: GroupResponsibleState): boolean {
  const activeRoom = getActiveRoom(state)
  if (!activeRoom)
    return false

  return state.selectedRoomParticipantIds.size > activeRoom.occupancy.max
}
